import { useRef, useEffect } from 'react'

function ChatPanel({ title, placeholder, inputPlaceholder = "Describe changes you'd like...", messages, input, onInputChange, onSend, loading, onClose, chatWidth, onPointerDown, isListening, startListening, stopListening, micSupported, interimText }) {
  const messagesEndRef = useRef(null)

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  const displayValue = interimText ? (input ? input + ' ' + interimText : interimText) : input

  return (
    <>
      <div className="chat-resize-handle" onMouseDown={onPointerDown} onTouchStart={onPointerDown} />
      <div className="schedule-chat-fixed" style={{ width: chatWidth }}>
        <div className="schedule-chat-fixed-header">
          <span>{title}</span>
          <button className="btn-icon schedule-chat-close" onClick={onClose}>✕</button>
        </div>
        <div className="schedule-chat-messages">
          {messages.length === 0 && (
            <p className="schedule-chat-placeholder">{placeholder}</p>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`schedule-chat-message schedule-chat-message--${msg.role}`}>
              <p className="schedule-chat-text">{msg.text}</p>
            </div>
          ))}
          {loading && (
            <div className="schedule-chat-message schedule-chat-message--assistant">
              <p className="schedule-chat-text schedule-chat-typing">Thinking...</p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <div className="schedule-chat-input-row">
          <input
            type="text"
            className="schedule-chat-input"
            placeholder={inputPlaceholder}
            value={displayValue}
            onChange={(e) => { if (!isListening) onInputChange(e.target.value) }}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && onSend()}
            onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 350)}
            disabled={loading}
          />
          {/* Desktop: show both buttons; Mobile: single combined mic/send button */}
          {micSupported && (
            <button
              className={`btn schedule-chat-mic schedule-chat-mic--desktop${isListening ? ' schedule-chat-mic--active' : ''}`}
              onClick={isListening ? stopListening : startListening}
              disabled={loading}
              aria-label={isListening ? 'Stop dictation' : 'Start dictation'}
              type="button"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="1" width="6" height="12" rx="3" />
                <path d="M5 10a7 7 0 0 0 14 0" />
                <line x1="12" y1="17" x2="12" y2="21" />
                <line x1="8" y1="21" x2="16" y2="21" />
              </svg>
            </button>
          )}
          <button
            className="btn btn-primary schedule-chat-send schedule-chat-send--desktop"
            onClick={onSend}
            disabled={loading || !input.trim()}
            aria-label="Send"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
          </button>
          {/* Mobile combined button: mic when empty, send when has text, stop when listening */}
          {isListening ? (
            <button
              className="btn schedule-chat-mic schedule-chat-mic--active schedule-chat-action--mobile"
              onClick={stopListening}
              type="button"
              aria-label="Stop dictation"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            </button>
          ) : input.trim() ? (
            <button
              className="btn btn-primary schedule-chat-send schedule-chat-action--mobile"
              onClick={onSend}
              disabled={loading}
              aria-label="Send"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            </button>
          ) : micSupported ? (
            <button
              className="btn schedule-chat-mic schedule-chat-action--mobile"
              onClick={startListening}
              disabled={loading}
              type="button"
              aria-label="Start dictation"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="1" width="6" height="12" rx="3" />
                <path d="M5 10a7 7 0 0 0 14 0" />
                <line x1="12" y1="17" x2="12" y2="21" />
                <line x1="8" y1="21" x2="16" y2="21" />
              </svg>
            </button>
          ) : (
            <button
              className="btn btn-primary schedule-chat-send schedule-chat-action--mobile"
              onClick={onSend}
              disabled={loading || !input.trim()}
              aria-label="Send"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </>
  )
}

export default ChatPanel
